/**
 * Admin API
 * API client for admin operations (superusers, organization settings, employee settings)
 */

import { apiClient } from "./apiClient";
import type {
  ISuperuser,
  ISuperuserCreateRequest,
  IUser,
  IOrganizationSettings,
  IServiceEmployeeSettings,
  IOrganization,
  IOrganizationInput,
  IOrganizationActivationInput,
  IEndpointGroup,
  IOrgEndpointGroups,
  IEndpointGroupAccessInput,
  IPage,
  IOrgPages,
  IPageAccessInput,
} from "./types";

export class AdminAPI {
  // ============ SUPERUSERS ============

  async getSuperusers(): Promise<ISuperuser[]> {
    const response = await apiClient.get<ISuperuser[]>("/superusers");
    if (response.status !== 200) {
      throw new Error(`Failed to fetch superusers: ${response.status}`);
    }
    return response.data;
  }

  async createSuperuser(data: ISuperuserCreateRequest): Promise<ISuperuser> {
    const response = await apiClient.put<ISuperuser>("/superusers", data);
    if (response.status !== 200 && response.status !== 201) {
      throw new Error(`Failed to create superuser: ${response.status}`);
    }
    return response.data;
  }

  async deleteSuperuser(email: string): Promise<void> {
    const response = await apiClient.delete<void>("/superusers", { email });
    if (response.status !== 200 && response.status !== 204) {
      throw new Error(`Failed to delete superuser: ${response.status}`);
    }
  }

  // ============ USERS (for autocomplete) ============

  async getUsers(): Promise<IUser[]> {
    const response = await apiClient.get<IUser[]>("/users");
    if (response.status !== 200) {
      throw new Error(`Failed to fetch users: ${response.status}`);
    }
    return response.data;
  }

  // ============ ORGANIZATION SETTINGS ============

  async getOrganizationSettings(): Promise<IOrganizationSettings> {
    const response = await apiClient.get<IOrganizationSettings>(
      "/organization/settings",
    );
    if (response.status !== 200) {
      throw new Error(
        `Failed to fetch organization settings: ${response.status}`,
      );
    }
    return response.data;
  }

  async updateOrganizationSettings(
    data: IOrganizationSettings,
  ): Promise<IOrganizationSettings> {
    const response = await apiClient.put<IOrganizationSettings>(
      "/organization/settings",
      data,
    );
    if (response.status !== 200) {
      throw new Error(
        `Failed to update organization settings: ${response.status}`,
      );
    }
    return response.data;
  }

  // ============ EMPLOYEE SETTINGS ============

  async getEmployeeSettings(
    employeeId: number,
  ): Promise<IServiceEmployeeSettings> {
    const response = await apiClient.get<IServiceEmployeeSettings>(
      `/employees/${employeeId}/settings`,
    );
    if (response.status !== 200) {
      throw new Error(`Failed to fetch employee settings: ${response.status}`);
    }
    return response.data;
  }

  async updateEmployeeSettings(
    employeeId: number,
    data: IServiceEmployeeSettings,
  ): Promise<IServiceEmployeeSettings> {
    const response = await apiClient.put<IServiceEmployeeSettings>(
      `/employees/${employeeId}/settings`,
      data,
    );
    if (response.status !== 200) {
      throw new Error(`Failed to update employee settings: ${response.status}`);
    }
    return response.data;
  }

  // ============ SUPERADMIN: ADMIN USERS ============

  /**
   * Get all admin users (users with admin role)
   */
  async getAdmins(): Promise<string[]> {
    const response = await apiClient.get<string[]>("/admins");
    if (response.status !== 200) {
      throw new Error(`Failed to fetch admins: ${response.status}`);
    }
    return response.data;
  }

  /**
   * Add a user as admin by email
   */
  async addAdmin(email: string): Promise<string[]> {
    const response = await apiClient.put<string[]>("/admins", { email });
    if (response.status !== 200 && response.status !== 201) {
      throw new Error(`Failed to add admin: ${response.status}`);
    }
    return response.data;
  }

  /**
   * Remove admin role from user by email
   */
  async deleteAdmin(email: string): Promise<string[]> {
    const response = await apiClient.delete<string[]>("/admins", { email });
    if (response.status !== 200 && response.status !== 204) {
      throw new Error(`Failed to delete admin: ${response.status}`);
    }
    return response.data;
  }

  /**
   * Get all users across all organizations (for autocomplete)
   */
  async getAllUsers(): Promise<IUser[]> {
    const response = await apiClient.get<IUser[]>("/users/all");
    if (response.status !== 200) {
      throw new Error(`Failed to fetch all users: ${response.status}`);
    }
    return response.data;
  }

  // ============ SUPERADMIN: ORGANIZATIONS ============

  /**
   * Get all organizations
   */
  async getOrganizations(): Promise<IOrganization[]> {
    const response = await apiClient.get<IOrganization[]>("/organizations");
    if (response.status !== 200) {
      throw new Error(`Failed to fetch organizations: ${response.status}`);
    }
    return response.data;
  }

  /**
   * Create or update an organization
   */
  async saveOrganization(data: IOrganizationInput): Promise<IOrganization[]> {
    const response = await apiClient.put<IOrganization[]>(
      "/organizations",
      data,
    );
    if (response.status !== 200 && response.status !== 201) {
      throw new Error(`Failed to save organization: ${response.status}`);
    }
    return response.data;
  }

  /**
   * Activate or deactivate an organization
   */
  async setOrganizationActivation(
    data: IOrganizationActivationInput,
  ): Promise<IOrganization[]> {
    const response = await apiClient.patch<IOrganization[]>(
      "/organizations/activation",
      data,
    );
    if (response.status !== 200) {
      throw new Error(
        `Failed to update organization activation: ${response.status}`,
      );
    }
    return response.data;
  }

  // ============ SUPERADMIN: ENDPOINT GROUPS ============

  /**
   * Get all endpoint groups
   */
  async getEndpointGroups(): Promise<IEndpointGroup[]> {
    const response = await apiClient.get<IEndpointGroup[]>("/endpoint/groups");
    if (response.status !== 200) {
      throw new Error(`Failed to fetch endpoint groups: ${response.status}`);
    }
    return response.data;
  }

  /**
   * Get all organization endpoint group access mappings
   */
  async getOrgEndpointGroups(): Promise<IOrgEndpointGroups[]> {
    const response = await apiClient.get<IOrgEndpointGroups[]>(
      "/organizations/endpoint/groups",
    );
    if (response.status !== 200) {
      throw new Error(
        `Failed to fetch organization endpoint groups: ${response.status}`,
      );
    }
    return response.data;
  }

  /**
   * Grant organization access to an endpoint group
   */
  async addEndpointGroupAccess(
    data: IEndpointGroupAccessInput,
  ): Promise<IOrgEndpointGroups[]> {
    const response = await apiClient.post<IOrgEndpointGroups[]>(
      "/organizations/endpoint/groups",
      data,
    );
    if (response.status !== 200 && response.status !== 201) {
      throw new Error(
        `Failed to add endpoint group access: ${response.status}`,
      );
    }
    return response.data;
  }

  /**
   * Revoke organization access to an endpoint group
   */
  async removeEndpointGroupAccess(
    data: IEndpointGroupAccessInput,
  ): Promise<IOrgEndpointGroups[]> {
    const response = await apiClient.delete<IOrgEndpointGroups[]>(
      "/organizations/endpoint/groups",
      data,
    );
    if (response.status !== 200 && response.status !== 204) {
      throw new Error(
        `Failed to remove endpoint group access: ${response.status}`,
      );
    }
    return response.data;
  }

  // ============ SUPERADMIN: PAGES ============

  /**
   * Get all pages
   */
  async getPages(): Promise<IPage[]> {
    const response = await apiClient.get<IPage[]>("/pages");
    if (response.status !== 200) {
      throw new Error(`Failed to fetch pages: ${response.status}`);
    }
    return response.data;
  }

  /**
   * Get all organization page access mappings
   */
  async getOrgPages(): Promise<IOrgPages[]> {
    const response = await apiClient.get<IOrgPages[]>("/organizations/pages");
    if (response.status !== 200) {
      throw new Error(`Failed to fetch organization pages: ${response.status}`);
    }
    return response.data;
  }

  /**
   * Grant organization access to a page
   */
  async addPageAccess(data: IPageAccessInput): Promise<IOrgPages[]> {
    const response = await apiClient.post<IOrgPages[]>(
      "/organizations/pages",
      data,
    );
    if (response.status !== 200 && response.status !== 201) {
      throw new Error(`Failed to add page access: ${response.status}`);
    }
    return response.data;
  }

  /**
   * Revoke organization access to a page
   */
  async removePageAccess(data: IPageAccessInput): Promise<IOrgPages[]> {
    const response = await apiClient.delete<IOrgPages[]>(
      "/organizations/pages",
      data,
    );
    if (response.status !== 200 && response.status !== 204) {
      throw new Error(`Failed to remove page access: ${response.status}`);
    }
    return response.data;
  }
}

// Singleton instance
export const adminAPI = new AdminAPI();
